// Callout Grain Background — WebGL ring/grain shader adapted from the design team's
// reference implementation (grain-circles.js). Renders a looping animated ring pattern
// into each Callout block's <canvas data-callout-grain-background>. Colors are resolved
// per-instance from the row's own themed CSS custom properties — --color-grain-accent
// (ring/grain color) and --color-bg (base color) — via getComputedStyle, so a page with
// several differently-themed Callout rows (calloutElement/calloutBlock share this same
// view + hook) each render correctly with no extra plumbing.
//
// Deviation from the reference script: prefers-reduced-motion here freezes the ENTIRE
// animation, not just the one-time intro burst. The reference only skips its intro and
// still loops the base ring forever; this renders exactly one static frame (agitation 0,
// ringPhase 0, introTime 0) and never calls requestAnimationFrame again, matching how
// interior-hero.js/card-carousel.js fully disable motion under reduced motion elsewhere
// in this codebase.
//
// No-WebGL fallback: if context creation or shader compilation fails (or the context is
// lost later), adds a `callout-grain-fallback` class to the canvas's parent container and
// stops that instance instead of throwing. The CSS for that fallback class is added in a
// later step.
//
// Pause/play control: each non-reduced-motion instance also reveals a
// [data-callout-grain-toggle] button (sibling of the canvas, matching how
// src/Rithm.Web/src/components/homepage-hero.js's video-toggle button works) so a viewer
// can stop the persistent animation themselves (WCAG 2.2.2), independent of the automatic
// off-screen pause below. The toggle's aria-pressed/icon reflect user intent only — it
// still reads "playing" while auto-paused off-screen, since the viewer didn't choose that.
//
// Visibility: a single IntersectionObserver (threshold 0.2) both triggers the one-time
// intro flourish AND gates the persistent requestAnimationFrame loop — the same crossing
// that reveals the canvas is the moment the intro plays and the loop starts, so there's no
// window where the calm loop is already running before the intro fires (that used to be
// two separate observers at two different thresholds, which could fire out of sync on a
// slow scroll and make the intro look like the animation restarting partway through).
// This also means a Callout scrolled below that same threshold pauses its rAF loop, so it
// stops costing GPU/battery while off-screen — this matters most on pages with several
// Callout blocks, since each owns its own independent WebGL context.

const canvases = /** @type {NodeListOf<HTMLCanvasElement>} */ (
    document.querySelectorAll('canvas[data-callout-grain-background]')
)

if (canvases.length) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Full-bleed Callout rows (Large.cshtml's canvas spans the entire row width, not
    // .container-capped) mean meaningfully more backing-buffer pixels per frame on wide/
    // ultrawide viewports than a container-capped canvas — clamp devicePixelRatio so the
    // shader isn't filling an unnecessarily huge buffer every frame.
    const MAX_DEVICE_PIXEL_RATIO = 2

    // Locked shader defaults, carried over from the reference implementation as-is.
    // colorA/colorB are intentionally absent here — they're resolved per-canvas-instance
    // below instead of hardcoded.
    const PARAMS = {
        speed: 0.077,
        centerX: 0.5,
        centerY: 0.5,
        ringSpacing: 0.425,
        barSolid: 0,
        barGrain: 0.48,
        grainMin: 0,
        grainMax: 0.105,
        grainScale: 0.8,
        grainSpill: 0.04,
        grainBlend: 0, // normal
        vignetteOpacity: 1,
        vignetteRadius: 0.79,
        vignetteSoftness: 0.5,
        vignetteWidth: 1.97,
        vignetteHeight: 0.54,
        vignetteY: 0.545,
        textGrainMin: 0,
        textGrainMax: 0,
        textGrainSpill: 0,
        textRingSpill: 0.5,
        introDuration: 3,
        introSettleDuration: 2,
        introSettleSpeed: 1.55,
        introWarpStrength: 0.2,
        introWarpScale: 8,
        introGrainDensity: 0.01,
        introRidgeDensity: 0.135,
        introRidgeSharpness: 2.4,
        introValleyFloor: 0.1,
        introFilamentScale: 2.6,
        introRingSpacing: 2.42,
        introSeparateSpeed: true,
        introSpeed: 0.05,
    }

    const INTRO_RAMP_IN = 0.3

    const VERT = `
        attribute vec2 a_pos;
        void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
    `

    // Note: no u_time uniform — the reference script wired one through, but nothing in
    // main() below actually reads it (only u_ringPhase/u_introTime drive the animation),
    // so it's dropped entirely here rather than carried over as dead plumbing.
    const FRAG = `
        precision highp float;
        uniform vec2  u_res;
        uniform vec3  u_colorA;
        uniform vec3  u_colorB;
        uniform float u_ringPhase;
        uniform float u_centerX;
        uniform float u_centerY;
        uniform float u_ringSpacing;
        uniform float u_introRingSpacing;
        uniform float u_barSolid;
        uniform float u_barGrain;
        uniform float u_grainMin;
        uniform float u_grainMax;
        uniform float u_grainScale;
        uniform float u_grainSpill;
        uniform int   u_grainBlend;
        uniform float u_vignetteOpacity;
        uniform float u_vignetteRadius;
        uniform float u_vignetteSoftness;
        uniform float u_vignetteWidth;
        uniform float u_vignetteHeight;
        uniform float u_vignetteY;
        uniform float u_textGrainMin;
        uniform float u_textGrainMax;
        uniform float u_textGrainSpill;
        uniform float u_textRingSpill;
        uniform float u_agitation;
        uniform float u_warpStrength;
        uniform float u_warpScale;
        uniform float u_introTime;
        uniform float u_introGrainDensity;
        uniform float u_introRidgeDensity;
        uniform float u_introRidgeSharpness;
        uniform float u_introValleyFloor;
        uniform float u_introFilamentScale;

        float hash21(vec2 p) {
            vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973));
            p3 += dot(p3, p3.yzx + 33.33);
            return fract((p3.x + p3.y) * p3.z);
        }

        float grainNoise(vec2 cell) {
            return fract(hash21(cell) + hash21(cell + 73.7) * 0.5);
        }

        float valueNoise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);
            f = f * f * (3.0 - 2.0 * f);
            float a = hash21(i);
            float b = hash21(i + vec2(1.0, 0.0));
            float c = hash21(i + vec2(0.0, 1.0));
            float d = hash21(i + vec2(1.0, 1.0));
            return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
        }

        float fbm(vec2 p) {
            float v = 0.0;
            float a = 0.5;
            mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
            for (int i = 0; i < 3; i++) {
                v += a * valueNoise(p);
                p = rot * p * 2.0 + 17.7;
                a *= 0.5;
            }
            return v;
        }

        float ridged(vec2 p) {
            float n = fbm(p);
            return 1.0 - abs(2.0 * n - 1.0);
        }

        vec3 blendGrain(vec3 base, vec3 grain, int mode) {
            if (mode == 1) return base * grain;
            if (mode == 2) return 1.0 - (1.0 - base) * (1.0 - grain);
            if (mode == 3) {
                return mix(
                    2.0 * base * grain,
                    1.0 - 2.0 * (1.0 - base) * (1.0 - grain),
                    step(0.5, base)
                );
            }
            if (mode == 4) return min(base, grain);
            if (mode == 5) return max(base, grain);
            if (mode == 6) return clamp(1.0 - (1.0 - base) / max(grain, 0.0001), 0.0, 1.0);
            if (mode == 7) return clamp(base / max(1.0 - grain, 0.0001), 0.0, 1.0);
            if (mode == 8) {
                return mix(
                    2.0 * base * grain,
                    1.0 - 2.0 * (1.0 - base) * (1.0 - grain),
                    step(0.5, grain)
                );
            }
            if (mode == 9) {
                return mix(
                    2.0 * base * grain + base * base * (1.0 - 2.0 * grain),
                    sqrt(base) * (2.0 * grain - 1.0) + 2.0 * base * (1.0 - grain),
                    step(0.5, grain)
                );
            }
            if (mode == 10) return abs(base - grain);
            if (mode == 11) return base + grain - 2.0 * base * grain;
            return grain;
        }

        vec3 applyGrain(vec3 base, vec3 grain, float d, int mode) {
            return mix(base, blendGrain(base, grain, mode), d);
        }

        void main() {
            vec2 uv = gl_FragCoord.xy / u_res;
            vec2 p = uv - vec2(u_centerX, u_centerY);
            p.x *= u_res.x / u_res.y;

            float agit = u_agitation;
            float dist0 = length(p);
            float introMask = smoothstep(0.1, 0.32, dist0) * (1.0 - smoothstep(0.48, 0.72, dist0));

            vec2 warpCoord = p * u_warpScale + vec2(u_introTime * 0.35, u_introTime * 0.22);
            vec2 warp = vec2(fbm(warpCoord), fbm(warpCoord + 31.4)) * 2.0 - 1.0;
            vec2 pWarp = p + warp * agit * u_warpStrength * mix(0.35, 1.0, introMask);

            float angle = atan(pWarp.y, pWarp.x);
            float radialWarp = ridged(vec2(angle * 2.5 + u_introTime * 0.15, length(pWarp) * u_warpScale * 1.5));
            pWarp += normalize(pWarp + vec2(0.0001)) * radialWarp * agit * u_warpStrength * 0.35 * introMask;

            float dist = mix(dist0, length(pWarp), agit);
            float t = u_ringPhase;
            float activeRingSpacing = mix(u_ringSpacing, u_introRingSpacing, agit);

            vec3 col;
            if (activeRingSpacing <= 0.0) {
                col = u_colorB;
            } else {
                float pos = fract((dist - t) / activeRingSpacing);
                float solid = clamp(u_barSolid, 0.0, 1.0);
                float grain = clamp(u_barGrain * (1.0 - agit * 0.2), 0.0, 1.0 - solid);
                float solidStart = 1.0 - solid;
                float grainStart = solidStart - grain;
                float effectiveSpill = u_grainSpill * (1.0 - agit * 0.9);
                float activeMax = mix(u_grainMax, u_introGrainDensity, agit);

                if (pos >= solidStart) {
                    col = u_colorA;
                } else {
                    float prob;
                    if (pos >= grainStart) {
                        float along = (pos - grainStart) / max(grain, 0.0001);
                        prob = mix(u_grainMin, activeMax, smoothstep(0.0, 1.0, along));
                    } else {
                        float spillProb = mix(u_grainMin, activeMax, 0.88);
                        prob = mix(u_grainMin, spillProb, effectiveSpill);
                    }
                    float filament = ridged(
                        mix(p, pWarp, agit) * u_warpScale * u_introFilamentScale * 1.2
                        + vec2(u_introTime * 0.18, -u_introTime * 0.11)
                    );
                    float filamentFine = ridged(
                        mix(p, pWarp, agit) * u_warpScale * u_introFilamentScale * 2.8
                        + vec2(-u_introTime * 0.12, u_introTime * 0.21)
                    );
                    float ridgeCoarse = pow(filament, u_introRidgeSharpness);
                    float ridgeFine = pow(filamentFine, u_introRidgeSharpness * 0.85);
                    float ridgeLine = max(ridgeCoarse, ridgeFine * 0.7);
                    ridgeLine = smoothstep(0.42, 0.92, ridgeLine);
                    float introProb = ridgeLine * u_introRidgeDensity;
                    prob = mix(prob, introProb, agit * introMask);
                    float depthMask = max(ridgeLine, introMask * 0.25);
                    prob *= mix(1.0, mix(u_introValleyFloor, 1.0, depthMask), agit);
                    vec2 cell = floor(gl_FragCoord.xy / max(u_grainScale, 0.5));
                    float g = grainNoise(cell);
                    float d = step(g, prob);
                    col = applyGrain(u_colorB, u_colorA, d, u_grainBlend);
                }
            }

            vec3 ringCol = col;

            vec2 vp = uv - vec2(u_centerX, u_vignetteY);
            vp.x *= u_res.x / u_res.y;
            vp.x /= max(u_vignetteWidth, 0.001);
            vp.y /= max(u_vignetteHeight, 0.001);

            float vignetteDist = length(vp);
            float vignette = smoothstep(
                u_vignetteRadius,
                max(u_vignetteRadius - u_vignetteSoftness, 0.0),
                vignetteDist
            );
            float vignetteMask = vignette * u_vignetteOpacity * (1.0 - agit);

            float textSpillProb = mix(u_textGrainMin, u_textGrainMax, 0.88);
            float textProb = mix(u_textGrainMin, textSpillProb, u_textGrainSpill);
            vec2 textCell = floor(gl_FragCoord.xy / max(u_grainScale, 0.5));
            float tg = grainNoise(textCell);
            vec3 textCol = applyGrain(u_colorB, u_colorA, step(tg, textProb), u_grainBlend);

            col = mix(ringCol, textCol, vignetteMask);
            col = mix(col, ringCol, u_textRingSpill * vignetteMask);

            gl_FragColor = vec4(col, 1.0);
        }
    `

    // Uniforms that never change once resolved at setup — set once in setStaticUniforms(),
    // not re-sent every frame. (u_res/u_grainScale are size-dependent, not truly constant,
    // so they're handled in resize() instead; u_ringPhase/u_agitation/u_introTime are the
    // only genuinely per-frame values, handled in draw().)
    const STATIC_UNIFORM_NAMES = [
        'colorA', 'colorB', 'centerX', 'centerY', 'ringSpacing', 'introRingSpacing',
        'barSolid', 'barGrain', 'grainMin', 'grainMax', 'grainSpill', 'grainBlend',
        'vignetteOpacity', 'vignetteRadius', 'vignetteSoftness', 'vignetteWidth',
        'vignetteHeight', 'vignetteY', 'textGrainMin', 'textGrainMax', 'textGrainSpill',
        'textRingSpill', 'warpStrength', 'warpScale', 'introGrainDensity',
        'introRidgeDensity', 'introRidgeSharpness', 'introValleyFloor', 'introFilamentScale',
    ]
    const RESIZE_UNIFORM_NAMES = ['res', 'grainScale']
    const FRAME_UNIFORM_NAMES = ['ringPhase', 'agitation', 'introTime']
    const UNIFORM_NAMES = [...STATIC_UNIFORM_NAMES, ...RESIZE_UNIFORM_NAMES, ...FRAME_UNIFORM_NAMES]

    /**
     * Parses a resolved CSS color string (#rgb, #rrggbb, or rgb()/rgba()) into a
     * [r, g, b] float triple in the 0..1 range expected by the shader's vec3 uniforms.
     * Falls through to black (with a warning) for syntax this doesn't recognize (e.g. a
     * future themes.postcss color authored as oklch()/space-separated rgb()) rather than
     * failing silently — a black ring/base is an obvious visual bug, not a subtle one.
     * @param {string} color
     */
    function parseColor(color) {
        const value = color.trim()
        if (value.startsWith('#')) {
            let hex = value.slice(1)
            if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('')
            const int = parseInt(hex, 16)
            return [((int >> 16) & 255) / 255, ((int >> 8) & 255) / 255, (int & 255) / 255]
        }
        const match = value.match(/rgba?\(([^)]+)\)/i)
        if (match) {
            const [r, g, b] = match[1].split(',').map((n) => parseFloat(n))
            return [r / 255, g / 255, b / 255]
        }
        console.warn(`[callout-grain-background] unrecognized color syntax, defaulting to black: "${value}"`)
        return [0, 0, 0]
    }

    /** @param {number} settleDuration @param {number} settleSpeed */
    function settlePhaseLength(settleDuration, settleSpeed) {
        return Math.max(settleDuration, 1 / Math.max(settleSpeed, 0.001))
    }

    /** @param {number} activeDuration @param {number} settleDuration @param {number} settleSpeed */
    function introTotalDuration(activeDuration, settleDuration, settleSpeed) {
        return INTRO_RAMP_IN + activeDuration + settlePhaseLength(settleDuration, settleSpeed)
    }

    /** @param {number} elapsed @param {number} activeDuration @param {number} settleDuration @param {number} settleSpeed */
    function computeAgitation(elapsed, activeDuration, settleDuration, settleSpeed) {
        const total = introTotalDuration(activeDuration, settleDuration, settleSpeed)
        if (elapsed >= total) return 0
        if (elapsed < INTRO_RAMP_IN) return elapsed / INTRO_RAMP_IN
        if (elapsed < INTRO_RAMP_IN + activeDuration) return 1

        const settleElapsed = elapsed - INTRO_RAMP_IN - activeDuration
        const settleLength = settlePhaseLength(settleDuration, settleSpeed)
        if (settleElapsed >= settleLength) return 0

        const blendT = Math.min(1, settleElapsed * settleSpeed)
        const eased = blendT * blendT * (3 - 2 * blendT)
        return 1 - eased
    }

    /**
     * @param {WebGLRenderingContext} gl
     * @param {number} type
     * @param {string} src
     * @returns {WebGLShader}
     */
    function compileShader(gl, type, src) {
        const shader = gl.createShader(type)
        if (!shader) throw new Error('callout-grain-background: createShader failed')
        gl.shaderSource(shader, src)
        gl.compileShader(shader)
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            const info = gl.getShaderInfoLog(shader)
            gl.deleteShader(shader)
            throw new Error(`callout-grain-background: shader compile failed: ${info}`)
        }
        return shader
    }

    /**
     * @typedef {{ hide: () => void }} ToggleHandle
     */

    /**
     * Wires up the [data-callout-grain-toggle] button that sits alongside `canvas` (same
     * DOM pattern as homepage-hero.js's video-toggle): reveals it once the animation is
     * actually running, and lets a viewer pause/resume it themselves (WCAG 2.2.2) —
     * independent of the automatic off-screen pausing in initCanvas, which never changes
     * the toggle's visible state since that's an internal optimization, not something the
     * viewer did.
     * @param {HTMLCanvasElement} canvas
     * @param {() => boolean} isPlaying
     * @param {(next: boolean) => void} setPlaying
     * @returns {ToggleHandle | null}
     */
    function setupToggle(canvas, isPlaying, setPlaying) {
        const toggle = /** @type {HTMLButtonElement?} */ (
            canvas.parentElement?.querySelector('[data-callout-grain-toggle]') ?? null
        )
        if (!toggle) {
            console.warn('[callout-grain-background] no [data-callout-grain-toggle] button found, pause control unavailable')
            return null
        }

        const icon = /** @type {SVGElement?} */ (toggle.querySelector('svg.icon'))
        const use = /** @type {SVGUseElement?} */ (icon?.querySelector('use') ?? null)
        if (!icon || !use) {
            console.warn('[callout-grain-background] toggle button is missing its icon/use markup, pause control unavailable')
            return null
        }

        const setVisualState = (/** @type {boolean} */ playing) => {
            use.setAttribute('xlink:href', playing ? '#icon-pause' : '#icon-play-outline')
            icon.classList.toggle('icon-pause', playing)
            icon.classList.toggle('icon-play-outline', !playing)
            toggle.setAttribute('aria-label', playing ? 'Pause background animation' : 'Play background animation')
            toggle.setAttribute('aria-pressed', String(playing))
        }

        const handleClick = () => {
            const next = !isPlaying()
            setPlaying(next)
            setVisualState(next)
        }
        toggle.addEventListener('click', handleClick)

        toggle.disabled = false
        toggle.classList.add('callout-grain-toggle--visible')
        setVisualState(true)

        return {
            hide() {
                toggle.removeEventListener('click', handleClick)
                toggle.disabled = true
                toggle.classList.remove('callout-grain-toggle--visible')
            },
        }
    }

    /** @param {HTMLCanvasElement} canvas */
    function initCanvas(canvas) {
        let rafId = 0
        let destroyed = false
        // Declared up front (not just in the non-reduced-motion branch below) so the
        // webglcontextlost listener — registered before the reduced-motion/full-motion
        // split, and possibly invoked long after either branch has run — can always safely
        // reference it, even for a reduced-motion instance that never reveals a toggle.
        /** @type {ToggleHandle | null} */
        let toggleHandle = null

        function applyFallback() {
            destroyed = true
            cancelAnimationFrame(rafId)
            rafId = 0
            const parent = canvas.parentElement
            if (parent) parent.classList.add('callout-grain-fallback')
        }

        try {
            const glContext = canvas.getContext('webgl')
            if (!glContext) throw new Error('callout-grain-background: WebGL not available')
            // Re-bound to a freshly-typed const: TS's null-narrowing from the check above
            // doesn't propagate into the nested functions defined below (resize/draw/etc.),
            // since it can't prove `gl` is still non-null whenever those actually run — this
            // gives every closure below a `WebGLRenderingContext`, not `... | null`.
            const gl = /** @type {WebGLRenderingContext} */ (glContext)

            // Not attempting context restoration (no webglcontextrestored handler) — a lost
            // context just permanently falls back to the static CSS background, same as a
            // failed initial setup, rather than trying to rebuild the whole GL program.
            canvas.addEventListener('webglcontextlost', () => {
                applyFallback()
                toggleHandle?.hide()
                console.warn('[callout-grain-background] WebGL context lost, falling back to static background')
            })

            const computed = getComputedStyle(canvas)
            const colorA = parseColor(computed.getPropertyValue('--color-grain-accent') || '#0D8ECE')
            const colorB = parseColor(computed.getPropertyValue('--color-bg') || '#14141A')

            const program = gl.createProgram()
            gl.attachShader(program, compileShader(gl, gl.VERTEX_SHADER, VERT))
            gl.attachShader(program, compileShader(gl, gl.FRAGMENT_SHADER, FRAG))
            gl.linkProgram(program)
            if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
                throw new Error(`callout-grain-background: program link failed: ${gl.getProgramInfoLog(program)}`)
            }
            gl.useProgram(program)

            const buffer = gl.createBuffer()
            gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
            const posLoc = gl.getAttribLocation(program, 'a_pos')
            gl.enableVertexAttribArray(posLoc)
            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0)

            /** @type {Record<string, WebGLUniformLocation | null>} */
            const u = {}
            UNIFORM_NAMES.forEach((name) => {
                u[name] = gl.getUniformLocation(program, `u_${name}`)
            })

            function setStaticUniforms() {
                const p = PARAMS
                gl.uniform3fv(u.colorA, colorA)
                gl.uniform3fv(u.colorB, colorB)
                gl.uniform1f(u.centerX, p.centerX)
                gl.uniform1f(u.centerY, p.centerY)
                gl.uniform1f(u.ringSpacing, p.ringSpacing)
                gl.uniform1f(u.introRingSpacing, p.introRingSpacing)
                gl.uniform1f(u.barSolid, p.barSolid)
                gl.uniform1f(u.barGrain, p.barGrain)
                gl.uniform1f(u.grainMin, p.grainMin)
                gl.uniform1f(u.grainMax, p.grainMax)
                gl.uniform1f(u.grainSpill, p.grainSpill)
                gl.uniform1i(u.grainBlend, p.grainBlend)
                gl.uniform1f(u.vignetteOpacity, p.vignetteOpacity)
                gl.uniform1f(u.vignetteRadius, p.vignetteRadius)
                gl.uniform1f(u.vignetteSoftness, p.vignetteSoftness)
                gl.uniform1f(u.vignetteWidth, Math.max(p.vignetteWidth, 0.001))
                gl.uniform1f(u.vignetteHeight, Math.max(p.vignetteHeight, 0.001))
                gl.uniform1f(u.vignetteY, p.vignetteY)
                gl.uniform1f(u.textGrainMin, p.textGrainMin)
                gl.uniform1f(u.textGrainMax, p.textGrainMax)
                gl.uniform1f(u.textGrainSpill, p.textGrainSpill)
                gl.uniform1f(u.textRingSpill, p.textRingSpill)
                gl.uniform1f(u.warpStrength, p.introWarpStrength)
                gl.uniform1f(u.warpScale, p.introWarpScale)
                gl.uniform1f(u.introGrainDensity, p.introGrainDensity)
                gl.uniform1f(u.introRidgeDensity, p.introRidgeDensity)
                gl.uniform1f(u.introRidgeSharpness, p.introRidgeSharpness)
                gl.uniform1f(u.introValleyFloor, p.introValleyFloor)
                gl.uniform1f(u.introFilamentScale, p.introFilamentScale)
            }

            function resize() {
                const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO)
                canvas.width = canvas.clientWidth * dpr
                canvas.height = canvas.clientHeight * dpr
                gl.viewport(0, 0, canvas.width, canvas.height)
                gl.uniform2f(u.res, canvas.width, canvas.height)
                const grainCellPx = canvas.clientWidth > 0 ? canvas.width / canvas.clientWidth : 1
                gl.uniform1f(u.grainScale, PARAMS.grainScale * grainCellPx)
            }

            /** @param {number} ringPhase @param {number} agitation @param {number} introTime */
            function draw(ringPhase, agitation, introTime) {
                gl.uniform1f(u.ringPhase, ringPhase)
                gl.uniform1f(u.agitation, agitation)
                gl.uniform1f(u.introTime, introTime)
                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
            }

            setStaticUniforms()
            resize()

            if (reducedMotion) {
                // Fully frozen: render once at rest (no intro, no ring motion) and never
                // schedule requestAnimationFrame again. Still track container resizes so
                // the static frame stays correctly proportioned, without ever animating.
                // No toggle is revealed here — there's nothing playing for a viewer to pause.
                draw(0, 0, 0)
                const staticResizeObserver = new ResizeObserver(() => {
                    resize()
                    draw(0, 0, 0)
                })
                staticResizeObserver.observe(canvas)
                return
            }

            /** @type {number | null} */
            let introStart = null
            let introHasPlayed = false
            let ringPhase = 0
            /** @type {number | null} */
            let lastFrameTime = null
            let userPaused = false
            let isVisible = false

            function shouldRun() {
                return !destroyed && !userPaused && isVisible
            }

            function scheduleFrame() {
                if (rafId === 0 && shouldRun()) rafId = requestAnimationFrame(frame)
            }

            // Reveals the pause/play toggle the first time the animation is confirmed
            // running (i.e. the canvas has actually been visible at least once) rather than
            // at setup time — setting it up eagerly would let a below-the-fold instance
            // announce "Pause" (aria-pressed="true") before a single frame has ever drawn.
            // Only ever runs once (toggleHandle stays set afterward).
            function revealToggleIfNeeded() {
                if (toggleHandle) return
                toggleHandle = setupToggle(
                    canvas,
                    () => !userPaused,
                    (playing) => {
                        userPaused = !playing
                        if (playing) {
                            lastFrameTime = null // avoid a large dt jump across the paused gap
                            scheduleFrame()
                        }
                    }
                )
            }

            // One observer drives both the one-time intro trigger AND the persistent
            // visibility-based play/pause — they used to be two separate observers at two
            // different thresholds (0 for visibility, 0.2 for the intro), which could fire
            // out of sync on a slow scroll: a sliver peeking into view (crossing the 0
            // threshold) started the calm loop immediately, then crossing 0.2 moments later
            // kicked off the intro's warp/settle flourish mid-loop — which reads as the
            // animation stopping and restarting, not as a one-time "greeting" as it scrolls
            // in. Using a single threshold means the intro and "start playing" are the same
            // event: nothing renders below the threshold, and the very first time it's
            // crossed, the intro plays while the loop is *just* starting, not partway through
            // an already-running calm loop. Off-screen pausing also now triggers at the same
            // 0.2 threshold (rather than only at fully 0% visible) — a bit more eager, which
            // only helps the GPU/battery-saving goal this observer exists for.
            const visibilityObserver = new IntersectionObserver(
                (entries) => {
                    const intersecting = entries[0]?.isIntersecting ?? false
                    isVisible = intersecting
                    if (!intersecting) return

                    if (!introHasPlayed) {
                        introStart = performance.now()
                        introHasPlayed = true
                    }
                    scheduleFrame()
                    revealToggleIfNeeded()
                },
                { threshold: 0.2 }
            )
            visibilityObserver.observe(canvas)

            const resizeObserver = new ResizeObserver(resize)
            resizeObserver.observe(canvas)

            function frame() {
                rafId = 0
                if (!shouldRun()) return

                const p = PARAMS
                const now = performance.now()

                const introElapsed = introStart !== null ? (now - introStart) / 1000 : 0
                const introTotal = introTotalDuration(p.introDuration, p.introSettleDuration, p.introSettleSpeed)
                const agitation = introStart !== null && introElapsed < introTotal
                    ? computeAgitation(introElapsed, p.introDuration, p.introSettleDuration, p.introSettleSpeed)
                    : 0

                const separateSpeed = p.introSeparateSpeed && agitation > 0
                const ringSpeed = separateSpeed ? p.speed + (p.introSpeed - p.speed) * agitation : p.speed
                const introTime = separateSpeed
                    ? introElapsed * (p.introSpeed / Math.max(Math.abs(p.speed), 0.0001))
                    : introElapsed

                const dt = lastFrameTime !== null ? (now - lastFrameTime) / 1000 : 0
                lastFrameTime = now
                ringPhase += dt * ringSpeed

                draw(ringPhase, agitation, introTime)
                rafId = requestAnimationFrame(frame)
            }
        } catch (error) {
            applyFallback()
            console.warn('[callout-grain-background] falling back to static background:', error)
        }
    }

    canvases.forEach(initCanvas)
}
